import React, { useState, useEffect } from 'react';
import { FileDown, Building2, Users, Calendar, ChevronRight, ChevronLeft, Plus, Trash2, CheckCircle } from 'lucide-react';
import ExcelJS from 'exceljs';
import DateInput from './DateInput';
import './AppointmentOrderForm.css';

// Mock data - matching FORM_XXIX.xlsx template placeholders exactly (22 total)
const mockFormData = {
  // Establishment fields
  est_name: "ABC Manufacturing Pvt Ltd",
  est_city: "Bangalore",

  // Date fields
  current_month: "January",
  current_year: "2025",
  payment_date: "2025-01-31",

  // Employee array (FORM XXIX tracks advances, damages, and fines)
  employees: [
    {
      emp_sno: "1",
      emp_id: "EMP001",
      emp_name: "Rajesh",
      emp_lname: "Kumar",
      emp_desig: "Machine Operator",

      // Advance Paid
      emp_adv_paid: "5000",
      emp_adv_noinst: "5",
      emp_adv_recdate: "2025-06-30",

      // Deduction for Damage/Loss
      emp_dmg: "Machine part damaged",
      emp_dmg_date: "2025-01-15",
      emp_dmg_amt: "2000",
      emp_dmg_noinst: "4",
      emp_dmg_recdate: "2025-05-31",

      // Fines
      emp_fine_ao: "Late reporting",
      emp_fine_notice: "2025-01-20",
      emp_fine_date: "2025-01-25",
      emp_fine_recdate: "2025-02-28"
    },
    {
      emp_sno: "2",
      emp_id: "EMP002",
      emp_name: "Anita",
      emp_lname: "Sharma",
      emp_desig: "Quality Inspector",

      emp_adv_paid: "3000",
      emp_adv_noinst: "3",
      emp_adv_recdate: "2025-04-30",

      emp_dmg: "",
      emp_dmg_date: "",
      emp_dmg_amt: "",
      emp_dmg_noinst: "",
      emp_dmg_recdate: "",

      emp_fine_ao: "",
      emp_fine_notice: "",
      emp_fine_date: "",
      emp_fine_recdate: ""
    }
  ]
};

const FormXXIXDeductionsExcel = () => {
  const [formData, setFormData] = useState(mockFormData);
  const [isGenerating, setIsGenerating] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    // Fetch data from API on component mount
    const fetchFormData = async () => {
      try {
        console.log('Fetching Form XXIX data from API...');
        // const response = await fetch('/api/form-xxix-data');
        // const data = await response.json();
        // setFormData(data);
        console.log('Using mock data for Form XXIX');
      } catch (error) {
        console.error('Error fetching Form XXIX data:', error);
        console.log('Falling back to mock data');
      }
    };

    fetchFormData();
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEmployeeChange = (index, field, value) => {
    const updatedEmployees = [...formData.employees];
    updatedEmployees[index] = {
      ...updatedEmployees[index],
      [field]: value
    };
    setFormData(prev => ({
      ...prev,
      employees: updatedEmployees
    }));
  };

  const addEmployee = () => {
    const newEmployee = {
      emp_sno: String(formData.employees.length + 1),
      emp_id: "",
      emp_name: "",
      emp_lname: "",
      emp_desig: "",
      emp_adv_paid: "",
      emp_adv_noinst: "",
      emp_adv_recdate: "",
      emp_dmg: "",
      emp_dmg_date: "",
      emp_dmg_amt: "",
      emp_dmg_noinst: "",
      emp_dmg_recdate: "",
      emp_fine_ao: "",
      emp_fine_notice: "",
      emp_fine_date: "",
      emp_fine_recdate: ""
    };
    setFormData(prev => ({
      ...prev,
      employees: [...prev.employees, newEmployee]
    }));
  };

  const removeEmployee = (index) => {
    if (formData.employees.length > 1) {
      const updatedEmployees = formData.employees.filter((_, idx) => idx !== index);
      setFormData(prev => ({ ...prev, employees: updatedEmployees }));
      // If we're on a step beyond the new employee count, go back
      if (currentStep > updatedEmployees.length) {
        setCurrentStep(updatedEmployees.length);
      }
    }
  };

  const nextStep = () => {
    const totalSteps = 1 + formData.employees.length; // 1 (organization) + employees
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const generateExcel = async () => {
    setIsGenerating(true);

    try {
      console.log('Starting Form XXIX (Deductions) Excel generation...');
      console.log('Form Data:', formData);

      // Load the template
      const response = await fetch('/templates/FORM_XXIX.xlsx');
      const arrayBuffer = await response.arrayBuffer();

      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(arrayBuffer);
      const worksheet = workbook.worksheets[0];

      console.log('Template loaded successfully');

      // Remove ALL background fills from the entire worksheet
      worksheet.eachRow({ includeEmpty: true }, (row) => {
        row.eachCell({ includeEmpty: true }, (cell) => {
          if (cell.fill) {
            cell.fill = null;
          }
        });
      });

      console.log('Background fills removed');

      // Two-pass placeholder replacement strategy

      // Pass 1: Replace form-level fields (organization, dates)
      console.log('Pass 1: Replacing form-level fields...');
      worksheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
        row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
          const cellValue = cell.value ? String(cell.value) : '';

          if (cellValue.includes('{{')) {
            const placeholderRegex = /\{\{\s*([^}]+?)\s*\}\}/g;
            let newValue = cellValue;
            let match;

            while ((match = placeholderRegex.exec(cellValue)) !== null) {
              const fieldName = match[1].trim();
              const normalizedFieldName = fieldName.replace(/\s+/g, '_');

              // Check form-level fields
              if (formData[normalizedFieldName] !== undefined) {
                console.log(`Row ${rowNumber}, Col ${colNumber}: Replacing {{ ${fieldName} }} with "${formData[normalizedFieldName]}"`);
                newValue = newValue.replace(match[0], formData[normalizedFieldName]);
              }
            }

            if (newValue !== cellValue) {
              cell.value = newValue;
            }
          }
        });
      });

      // Pass 2: Replace employee-specific fields for the first employee
      console.log('Pass 2: Replacing employee fields...');
      if (formData.employees && formData.employees.length > 0) {
        const employee = formData.employees[0];

        worksheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
          row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
            const cellValue = cell.value ? String(cell.value) : '';

            if (cellValue.includes('{{')) {
              const placeholderRegex = /\{\{\s*([^}]+?)\s*\}\}/g;
              let newValue = cellValue;
              let match;

              while ((match = placeholderRegex.exec(cellValue)) !== null) {
                const fieldName = match[1].trim();
                const normalizedFieldName = fieldName.replace(/\s+/g, '_');

                // Check employee fields
                if (employee[normalizedFieldName] !== undefined) {
                  console.log(`Row ${rowNumber}, Col ${colNumber}: Replacing {{ ${fieldName} }} with "${employee[normalizedFieldName]}"`);
                  newValue = newValue.replace(match[0], employee[normalizedFieldName]);
                }
              }

              if (newValue !== cellValue) {
                cell.value = newValue;
              }
            }
          });
        });
      }

      console.log('All placeholders replaced');

      // Generate Form file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Form-XXIX-Deductions-${formData.current_month}-${formData.current_year}-${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      console.log('Excel file generated and downloaded successfully');

      setTimeout(() => {
        setIsGenerating(false);
        alert('Form XXIX - Deductions Register Excel generated successfully!');
      }, 500);

    } catch (error) {
      console.error('Error generating Excel:', error);
      alert(`Error generating Excel: ${error.message}`);
      setIsGenerating(false);
    }
  };

  // Calculate total steps: 1 (organization) + employees
  const totalSteps = 1 + formData.employees.length;
  const progress = ((currentStep + 1) / totalSteps) * 100;

  // Render step content
  const renderStepContent = () => {
    // Step 0: Organization & Period
    if (currentStep === 0) {
      return (
        <div className="form-section">
          <div className="section-header">
            <Building2 size={20} />
            <h3>Organization & Period Information</h3>
          </div>
          <div className="form-grid">
            <div className="form-field">
              <label>Organization Name</label>
              <input
                type="text"
                value={formData.est_name}
                onChange={(e) => handleInputChange('est_name', e.target.value)}
              />
            </div>
            <div className="form-field">
              <label>City</label>
              <input
                type="text"
                value={formData.est_city}
                onChange={(e) => handleInputChange('est_city', e.target.value)}
              />
            </div>
            <div className="form-field">
              <label>Month</label>
              <select
                value={formData.current_month}
                onChange={(e) => handleInputChange('current_month', e.target.value)}
              >
                {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map(month => (
                  <option key={month} value={month}>{month}</option>
                ))}
              </select>
            </div>
            <div className="form-field">
              <label>Year</label>
              <input
                type="number"
                value={formData.current_year}
                onChange={(e) => handleInputChange('current_year', e.target.value)}
              />
            </div>
            <div className="form-field">
              <label>Payment Date</label>
              <DateInput
                value={formData.payment_date}
                onChange={(e) => handleInputChange('payment_date', e.target.value)}
              />
            </div>
          </div>
        </div>
      );
    }

    // Steps 1 to N: Employee details
    if (currentStep > 0 && currentStep <= formData.employees.length) {
      const employeeIndex = currentStep - 1;
      const employee = formData.employees[employeeIndex];

      return (
        <div className="form-section" style={{ background: 'var(--theme-surface)', border: '1px solid #e5e7eb' }}>
          <div className="section-header" style={{ justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Users size={20} />
              <h3>Employee {employeeIndex + 1} - Deduction Details</h3>
            </div>
            {formData.employees.length > 1 && (
              <button
                onClick={() => removeEmployee(employeeIndex)}
                style={{
                  background: '#ef4444',
                  color: 'var(--theme-surface, #ffffff)',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '0.5rem 1rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontSize: '0.875rem'
                }}
              >
                <Trash2 size={16} />
                Remove Employee
              </button>
            )}
          </div>

          {/* Basic Information */}
          <div style={{ marginBottom: '1.5rem' }}>
            <h4 style={{ fontSize: '0.95rem', fontWeight: '600', marginBottom: '0.75rem', color: '#374151' }}>
              Basic Information
            </h4>
            <div className="form-grid">
              <div className="form-field">
                <label>Serial No.</label>
                <input
                  type="text"
                  value={employee.emp_sno}
                  onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_sno', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>Employee ID</label>
                <input
                  type="text"
                  value={employee.emp_id}
                  onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_id', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>First Name</label>
                <input
                  type="text"
                  value={employee.emp_name}
                  onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_name', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>Last Name</label>
                <input
                  type="text"
                  value={employee.emp_lname}
                  onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_lname', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>Designation</label>
                <input
                  type="text"
                  value={employee.emp_desig}
                  onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_desig', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Advance Paid */}
          <div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#dbeafe', borderRadius: '6px' }}>
            <h4 style={{ fontSize: '0.95rem', fontWeight: '600', marginBottom: '0.75rem', color: '#1e40af', margin: 0 }}>
              Advance Paid
            </h4>
            <div className="form-grid" style={{ marginTop: '0.75rem' }}>
              <div className="form-field">
                <label>Amount Paid (Rs.)</label>
                <input
                  type="number"
                  value={employee.emp_adv_paid}
                  onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_adv_paid', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>No. of Instalments</label>
                <input
                  type="number"
                  value={employee.emp_adv_noinst}
                  onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_adv_noinst', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>Recovery Completion Date</label>
                <DateInput
                  value={employee.emp_adv_recdate}
                  onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_adv_recdate', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Deduction for Damage/Loss */}
          <div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#fee2e2', borderRadius: '6px' }}>
            <h4 style={{ fontSize: '0.95rem', fontWeight: '600', marginBottom: '0.75rem', color: '#991b1b', margin: 0 }}>
              Deduction for Damage/Loss
            </h4>
            <div className="form-grid" style={{ marginTop: '0.75rem' }}>
              <div className="form-field">
                <label>Damage/Loss Description</label>
                <input
                  type="text"
                  value={employee.emp_dmg}
                  onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_dmg', e.target.value)}
                  placeholder="Describe damage or loss"
                />
              </div>
              <div className="form-field">
                <label>Show Cause Notice Date</label>
                <DateInput
                  value={employee.emp_dmg_date}
                  onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_dmg_date', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>Total Amount (Rs.)</label>
                <input
                  type="number"
                  value={employee.emp_dmg_amt}
                  onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_dmg_amt', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>No. of Instalments</label>
                <input
                  type="number"
                  value={employee.emp_dmg_noinst}
                  onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_dmg_noinst', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>Recovery Completion Date</label>
                <DateInput
                  value={employee.emp_dmg_recdate}
                  onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_dmg_recdate', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Fines */}
          <div style={{ padding: '1rem', background: '#fef3c7', borderRadius: '6px' }}>
            <h4 style={{ fontSize: '0.95rem', fontWeight: '600', marginBottom: '0.75rem', color: '#92400e', margin: 0 }}>
              Fines
            </h4>
            <div className="form-grid" style={{ marginTop: '0.75rem' }}>
              <div className="form-field">
                <label>Act or Omission</label>
                <input
                  type="text"
                  value={employee.emp_fine_ao}
                  onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_fine_ao', e.target.value)}
                  placeholder="Reason for fine"
                />
              </div>
              <div className="form-field">
                <label>Show Cause Notice Date</label>
                <DateInput
                  value={employee.emp_fine_notice}
                  onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_fine_notice', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>Fine Imposed Date</label>
                <DateInput
                  value={employee.emp_fine_date}
                  onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_fine_date', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>Recovery Completion Date</label>
                <DateInput
                  value={employee.emp_fine_recdate}
                  onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_fine_recdate', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Add Another Employee Button */}
          <button
            onClick={() => {
              addEmployee();
              // Move to the new employee's step
              setCurrentStep(formData.employees.length + 1);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              backgroundColor: '#10b981',
              color: 'var(--theme-surface, #ffffff)',
              border: 'none',
              borderRadius: '6px',
              padding: '0.75rem 1.5rem',
              fontSize: '0.95rem',
              cursor: 'pointer',
              marginTop: '1.5rem',
              width: '100%',
              justifyContent: 'center'
            }}
          >
            <Plus size={20} />
            Add Another Employee
          </button>
        </div>
      );
    }

    // No final review step
    return null;
  };

  return (
    <div className="appointment-order-container">
      <div className="appointment-header">
        <div>
          <h2 className="appointment-title">Form XXIX - Deductions Register</h2>
          <p className="appointment-subtitle">Register of Advances, Deductions for Damages or Loss and Fines</p>
        </div>
      </div>

      <div className="appointment-content">
        {/* Progress Bar */}
        <div style={{ marginBottom: '2rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
            <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
              Step {currentStep + 1} of {totalSteps}
            </span>
            <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
              {currentStep === 0 ? 'Organization & Period' : `Employee ${currentStep}`}
            </span>
          </div>
          <div style={{ width: '100%', height: '8px', background: '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
            <div style={{
              width: `${progress}%`,
              height: '100%',
              background: 'linear-gradient(to right, #3b82f6, #8b5cf6)',
              transition: 'width 0.3s ease'
            }} />
          </div>
        </div>

        {/* Step Content */}
        {renderStepContent()}

        {/* Navigation Buttons */}
        <div className="form-actions" style={{ display: 'flex', justifyContent: 'space-between', marginTop: '2rem' }}>
          <button
            onClick={prevStep}
            disabled={currentStep === 0}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1.5rem',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              background: currentStep === 0 ? '#f8f9fa' : 'var(--theme-surface, #ffffff)',
              color: currentStep === 0 ? '#9ca3af' : '#374151',
              cursor: currentStep === 0 ? 'not-allowed' : 'pointer',
              fontSize: '0.95rem',
              fontWeight: '500'
            }}
          >
            <ChevronLeft size={20} />
            Previous
          </button>

          {currentStep === totalSteps - 1 ? (
            <button
              onClick={generateExcel}
              disabled={isGenerating}
              className={`btn-generate ${isGenerating ? 'generating' : ''}`}
            >
              {isGenerating ? (
                <>
                  <svg className="spinner" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" opacity="0.25"/>
                    <path d="M12 2a10 10 0 0 1 0 20" opacity="0.75"/>
                  </svg>
                  <span>Generating Excel...</span>
                </>
              ) : (
                <>
                  <FileDown size={20} />
                  <span>Generate Form</span>
                </>
              )}
            </button>
          ) : (
            <button
              onClick={nextStep}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1.5rem',
                borderRadius: '6px',
                border: 'none',
                background: 'linear-gradient(to right, #3b82f6, #8b5cf6)',
                color: 'var(--theme-surface, #ffffff)',
                cursor: 'pointer',
                fontSize: '0.95rem',
                fontWeight: '500'
              }}
            >
              Next
              <ChevronRight size={20} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FormXXIXDeductionsExcel;
